import React from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ArrowRight, Play, CheckCircle } from 'lucide-react';

const Hero = () => {
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const benefits = [
    'Save 30% effort & time daily',
    'Accurate cost calculation',
    'Real-time task tracking'
  ];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-16 lg:pt-20">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-hero opacity-10"></div>
      
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, hsl(var(--primary)) 1px, transparent 0)`,
          backgroundSize: '24px 24px'
        }}></div>
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left Content */}
          <div className="space-y-8 text-center lg:text-left animate-fade-in">
            {/* Beta Badge */}
            <div className="flex justify-center lg:justify-start">
              <Badge variant="secondary" className="px-4 py-2 bg-gradient-primary text-white border-0 hover:shadow-warm transition-all duration-300">
                🚀 Beta Launch - Limited Time Offer
              </Badge>
            </div>

            {/* Main Headline */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight">
                Transform Your
                <span className="text-gradient-hero block animate-gradient-shift bg-200%">
                  Project Management
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl">
                Track employee tasks, calculate project costs, and boost team productivity by 30% with EfforTrak's intelligent project management solution.
              </p>
            </div>

            {/* Benefits List */}
            <div className="space-y-3">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center justify-center lg:justify-start space-x-3">
                  <CheckCircle className="h-5 w-5 text-accent flex-shrink-0" />
                  <span className="text-muted-foreground font-medium">{benefit}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button 
                onClick={() => scrollToSection('contact')}
                size="lg" 
                className="bg-gradient-warm hover:shadow-warm transition-all duration-300 group px-8 py-6 text-base text-white"
              >
                Start Free Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button 
                onClick={() => {
                  const video = document.querySelector('iframe[title="EffTrak Demo Video"]');
                  if (video) {
                    video.scrollIntoView({ behavior: 'smooth', block: 'center' });
                  }
                }}
                variant="outline" 
                size="lg" 
                className="border-primary/30 text-primary hover:border-primary hover:bg-primary/10 transition-all duration-300 group px-8 py-6 text-base"
              >
                <Play className="mr-2 h-5 w-5 group-hover:scale-110 transition-transform" />
                Watch Demo
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center justify-center lg:justify-start space-x-6 text-sm text-muted-foreground pt-4">
              <div className="flex items-center space-x-1">
                <span className="font-semibold text-accent">✓</span>
                <span>Free Setup</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="font-semibold text-accent">✓</span>
                <span>No Credit Card</span>
              </div>
              <div className="flex items-center space-x-1">
                <span className="font-semibold text-accent">✓</span>
                <span>Cancel Anytime</span>
              </div>
            </div>
          </div>

          {/* Right Content - Hero Video */}
          <div className="relative animate-slide-in-right">
            <div className="relative rounded-2xl overflow-hidden shadow-elegant hover-lift">
              {/* YouTube Video Embed */}
              <div className="relative pb-[56.25%] h-0"> {/* 16:9 Aspect Ratio */}
                <iframe
                  className="absolute top-0 left-0 w-full h-full rounded-2xl"
                  src="https://www.youtube.com/embed/6mddONCiYc8?rel=0&modestbranding=1&showinfo=0"
                  title="EffTrak Demo Video"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                ></iframe>
              </div>
              
              {/* Floating elements */}
              <div className="absolute top-4 right-4 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-card z-10">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-pink rounded-full animate-pulse"></div>
                  <span className="text-xs font-medium text-muted-foreground">Live Demo</span>
                </div>
              </div>
              
              <div className="absolute bottom-4 left-4 bg-background/90 backdrop-blur-sm rounded-lg px-3 py-2 shadow-card z-10">
                <div className="flex items-center space-x-2">
                  <span className="text-xs font-medium text-muted-foreground">Watch Now:</span>
                  <span className="text-sm font-bold text-orange">EffTrak Demo</span>
                </div>
              </div>
            </div>

            {/* Background decoration */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-yellow/20 rounded-full blur-xl"></div>
            <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-pink/20 rounded-full blur-xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;